import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Check } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const MagicLinkPage = () => {
  const [params] = useSearchParams();
  const initialEmail = params.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState(null);

  // If URL has ?t=<token>, verify it and log in
  const token = params.get('t');
  const { refresh } = useAuth();
  const [verifying, setVerifying] = useState(!!token);
  const [verifyError, setVerifyError] = useState('');
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/auth/magic-link/verify?t=${encodeURIComponent(token)}`);
        // Persist under the same key that api.js reads on subsequent requests.
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user || {}));
        if (refresh) await refresh();
        // Role-aware landing. Magic-link users without a password go to /auth/set-password.
        const role = data.user && data.user.role;
        const needsPassword = data.user && data.user.has_password === false;
        if (needsPassword) {
          window.location.href = '/auth/set-password';
        } else {
          window.location.href = (role === 'admin' || role === 'super_admin') ? '/admin' : '/my-events';
        }
      } catch (e) {
        setVerifyError(e?.response?.data?.detail || 'Link no longer valid');
      } finally { setVerifying(false); }
    })();
  }, [token]);

  const request = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/magic-link', { email });
      setSent(true);
      setFallbackUrl(data?.magic_url_fallback || null);
    } finally { setLoading(false); }
  };

  if (token) {
    return (
      <div className="container mx-auto px-4 py-24">
        <Card className="max-w-md mx-auto border shadow-soft">
          <CardContent className="p-10 text-center">
            {verifying ? (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700 mx-auto" />
            ) : verifyError ? (
              <>
                <h1 className="font-display text-2xl font-bold text-primary-900 mb-2">Link invalid</h1>
                <p className="text-neutral-600 mb-6">{verifyError}</p>
                <Button asChild variant="outline"><Link to="/auth/magic-link">Request a new link</Link></Button>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-24">
      <Card className="max-w-md mx-auto border shadow-soft">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 flex items-center justify-center"><Mail className="h-5 w-5 text-secondary-700" /></div>
            <h1 className="font-display text-2xl font-bold text-primary-900">Sign in with email</h1>
          </div>
          <p className="text-neutral-600 text-sm mb-5">We'll email you a one-tap sign-in link. No password required.</p>

          {sent ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-success-50 border border-success-200 text-success-800 text-sm">
                <Check className="h-4 w-4 mt-0.5" /> Check your inbox for a sign-in link.
              </div>
              {fallbackUrl && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="text-xs text-yellow-800 font-semibold uppercase tracking-widest mb-1">Testing link (email not sent in this env)</div>
                  <a href={fallbackUrl} className="text-sm text-primary-800 break-all underline">{fallbackUrl}</a>
                </div>
              )}
            </div>
          ) : (
            <>
              <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mb-4" />
              <Button onClick={request} disabled={!email || loading} className="w-full bg-primary-800 hover:bg-primary-900 text-white">
                {loading ? 'Sending…' : 'Send me a link'}
              </Button>
              <div className="mt-4 text-center text-xs text-neutral-500">
                Prefer a password? <Link to="/auth/login" className="text-primary-800 hover:underline">Sign in with password</Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLinkPage;
